/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */
import { registerI18nLoader } from '@ui5/webcomponents-base/dist/asset-registries/i18n.js';
import '@ui5/webcomponents-react/dist/Assets';
import parse from '@ui5/webcomponents-base/dist/PropertiesFileFormat.js';
import { setFetchDefaultLanguage, setLanguage } from '@ui5/webcomponents-base/dist/config/Language.js';
import * as storageUtils from '../utils/storageUtils';
import { browserLanguage, languageMap, languagesSupported } from '../commons';

const initialLanguages: string[] = ['3q', '2q'];
const supportedLangCodes: string[] = [...initialLanguages, ...languagesSupported.map(lang => lang.code.toLowerCase())];
const isFallbackLanguage = (langCode: string): boolean => {
    return !supportedLangCodes.includes(langCode) || langCode.startsWith('en');
};

const getStoredOrBrowserLanguage = (): string => {
    const urlParams = new URLSearchParams(window.location.search);
    const sapLanguage = urlParams.get('sap-language');
    let langCode: string = sapLanguage ?? (storageUtils.getItem('user')?.locale || browserLanguage).toLowerCase();

    if (isFallbackLanguage(langCode)) {
        return 'fallback';
    }
    langCode = languageMap[langCode] || langCode;
    return langCode;
};

const registerFallBackBundle = (langCode: string): void => {
    registerI18nLoader('i18n', langCode.replace('_browser', ''), async () => {
        const props = await (await fetch(`/assets/i18n.properties`)).text();
        return parse(props);
    });
};

const registerLanguageBundle = (): void => {
    supportedLangCodes.forEach(langCode => {
        langCode = languageMap[langCode] || langCode;
        const langFile = langCode === 'fallback' ? 'i18n' : `i18n_${langCode.replace('_browser', '')}`;
        const url = `/assets/${langFile}.properties`;
        registerI18nLoader('myApp', langCode, async () => {
            const props = await (await fetch(url)).text();
            return parse(props);
        });
    });
};

const getTextKeyByLanguageCode = (code: string): string => {
    const language = languagesSupported.find(lang => lang?.code === code);
    if (!language) {
        return 'ENGLISH';
    }
    return language.textKey;
};

const getLanguageCodeByTextKey = (textKey: string) => {
    const language = languagesSupported.find(lang => lang.textKey === textKey);
    return language ? language.code : null;
};

const initializeLanguageBundles = async () => {
    const langCode = getStoredOrBrowserLanguage();
    registerLanguageBundle();
    registerFallBackBundle(langCode);
    await setLanguage(langCode);
    setFetchDefaultLanguage(true);
};

export {
    languagesSupported,
    initializeLanguageBundles,
    getStoredOrBrowserLanguage,
    registerFallBackBundle,
    registerLanguageBundle,
    getTextKeyByLanguageCode,
    getLanguageCodeByTextKey,
    isFallbackLanguage,
};
